import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Building2, Activity, Pencil, Plus, Save, Trash2, ArrowDown, ArrowLeft, ArrowUp, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import api from '../services/api';
import { formatDate, formatCurrency } from '../utils/formatters';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'suspended', label: 'Suspended' },
];

const STATUS_BADGES = {
  pending: 'admin-badge-warning',
  approved: 'admin-badge-success',
  suspended: 'admin-badge-danger',
};

function groupOperatingHours(operatingHours = []) {
  return Object.entries(operatingHours.reduce((groups, hour) => {
    if (!groups[hour.dayOfWeek]) groups[hour.dayOfWeek] = [];
    groups[hour.dayOfWeek].push(hour);
    return groups;
  }, {}));
}

export default function VenueApproval() {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const [venues, setVenues] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [approvedPartnerAccounts, setApprovedPartnerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('approved');
  const [actionLoading, setActionLoading] = useState(null);
  const [detailVenue, setDetailVenue] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingVenue, setEditingVenue] = useState(false);
  const [venueForm, setVenueForm] = useState({});
  const [courtForm, setCourtForm] = useState({ name: '', courtType: 'Indoor', isActive: true });
  const [editingCourtId, setEditingCourtId] = useState(null);
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [deletingCourtId, setDeletingCourtId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhotoUrl, setDeletingPhotoUrl] = useState(null);
  const [reorderingCourts, setReorderingCourts] = useState(false);

  const loadVenues = useCallback(async () => {
    setLoading(true);
    try {
      const [data, applications, approvedAccounts] = await Promise.all([
        activeTab === 'pending'
          ? adminService.getPendingVenues()
          : adminService.getVenues(activeTab),
        adminService.getVenuePartnerApplications(),
        activeTab === 'approved'
          ? adminService.getVenuePartnerApplications('approved')
          : Promise.resolve([]),
      ]);
      setVenues(Array.isArray(data) ? data : []);
      setPartnerApplications(Array.isArray(applications) ? applications : []);
      setApprovedPartnerAccounts(Array.isArray(approvedAccounts) ? approvedAccounts : []);
    } catch (err) {
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!venueId) loadVenues();
  }, [loadVenues, venueId]);

  const handleApprove = async (venueId) => {
    setActionLoading(venueId);
    try {
      await adminService.approveVenue(venueId);
      setVenues((prev) => prev.filter((v) => v.id !== venueId));
      toast.success('Venue approved successfully');
    } catch (err) {
      toast.error('Failed to approve venue');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (venueId) => {
    setActionLoading(venueId);
    try {
      await adminService.suspendVenue(venueId);
      setVenues((prev) => prev.filter((v) => v.id !== venueId));
      toast.success('Venue suspended');
    } catch (err) {
      toast.error('Failed to suspend venue');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprovePartnerApplication = async (userId) => {
    setActionLoading(`partner-${userId}`);
    try {
      await adminService.approveVenuePartnerApplication(userId);
      await loadVenues();
      toast.success('Venue partner account approved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve venue partner account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclinePartnerApplication = async (userId) => {
    setActionLoading(`partner-${userId}`);
    try {
      await adminService.declineVenuePartnerApplication(userId);
      setPartnerApplications((items) => items.filter((item) => item.id !== userId));
      toast.success('Venue partner application declined');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to decline venue partner application');
    } finally {
      setActionLoading(null);
    }
  };

  async function openDetail(venueId) {
    setDetailLoading(true);
    setDetailVenue(null);
    try {
      const [venueRes, pricingRes] = await Promise.all([
        api.get(`/venues/${venueId}`),
        api.get(`/venues/${venueId}/pricing`),
      ]);
      setDetailVenue({ ...venueRes.data, pricingRules: Array.isArray(pricingRes.data) ? pricingRes.data : [] });
      setVenueForm({
        name: venueRes.data.name || '', description: venueRes.data.description || '',
        address: venueRes.data.address || '', city: venueRes.data.city || '',
        state: venueRes.data.state || '', pricePerHour: venueRes.data.pricePerHour ?? '',
        latitude: venueRes.data.latitude?.toString() || '',
        longitude: venueRes.data.longitude?.toString() || '',
        absorbsPaymentFees: Boolean(venueRes.data.absorbsPaymentFees),
        payoutPolicy: venueRes.data.payoutPolicy || 'WeeklyAfterCompletion',
        maxBookingHoursPerTransaction: venueRes.data.maxBookingHoursPerTransaction?.toString() || '',
        supportsQrPhPayments: venueRes.data.supportsQrPhPayments !== false,
        supportsGCashPayments: venueRes.data.supportsGCashPayments !== false,
      });
      setEditingVenue(false);
      setEditingCourtId(null);
      setShowCourtForm(false);
      setDeletingCourtId(null);
      setDeletingPhotoUrl(null);
    } catch {
      toast.error('Failed to load venue details');
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (venueId) openDetail(venueId);
  }, [venueId]);

  const saveVenue = async (event) => {
    event.preventDefault();
    const hasLatitude = String(venueForm.latitude ?? '').trim() !== '';
    const hasLongitude = String(venueForm.longitude ?? '').trim() !== '';
    const latitude = Number(venueForm.latitude);
    const longitude = Number(venueForm.longitude);

    if (hasLatitude !== hasLongitude) {
      toast.error('Enter both latitude and longitude');
      return;
    }
    if (hasLatitude && (!Number.isFinite(latitude) || latitude < -90 || latitude > 90 || !Number.isFinite(longitude) || longitude < -180 || longitude > 180)) {
      toast.error('Use a latitude from -90 to 90 and a longitude from -180 to 180');
      return;
    }
    const maximumHours = venueForm.maxBookingHoursPerTransaction === '' ? 0 : Number(venueForm.maxBookingHoursPerTransaction);
    if (!Number.isInteger(maximumHours) || maximumHours < 0 || maximumHours > 24) {
      toast.error('Maximum booking hours must be a whole number between 1 and 24, or blank for unlimited');
      return;
    }
    if (!venueForm.supportsQrPhPayments && !venueForm.supportsGCashPayments) {
      toast.error('Select at least one online payment method');
      return;
    }

    setSavingDetails(true);
    try {
      const payload = { ...venueForm, pricePerHour: Number(venueForm.pricePerHour), maxBookingHoursPerTransaction: maximumHours };
      if (hasLatitude) {
        payload.latitude = latitude;
        payload.longitude = longitude;
      } else {
        delete payload.latitude;
        delete payload.longitude;
      }
      await api.put(`/venues/${detailVenue.id}`, payload);
      toast.success('Venue details updated');
      await openDetail(detailVenue.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update venue');
    } finally {
      setSavingDetails(false);
    }
  };

  const saveCourt = async (event) => {
    event.preventDefault();
    setSavingDetails(true);
    try {
      if (editingCourtId) {
        await api.put(`/courts/${editingCourtId}`, courtForm);
        toast.success('Court updated');
      } else {
        await api.post(`/venues/${detailVenue.id}/courts`, courtForm);
        toast.success('Court added');
      }
      setCourtForm({ name: '', courtType: 'Indoor', isActive: true });
      setEditingCourtId(null);
      setShowCourtForm(false);
      await openDetail(detailVenue.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save court');
    } finally {
      setSavingDetails(false);
    }
  };

  const deleteCourt = async (courtId) => {
    setSavingDetails(true);
    try {
      const response = await api.delete(`/courts/${courtId}`);
      toast.success(response.data?.message || 'Court deleted');
      await openDetail(detailVenue.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete court');
    } finally {
      setSavingDetails(false);
    }
  };

  const moveCourt = async (courtId, direction) => {
    if (!detailVenue || reorderingCourts) return;
    const courts = detailVenue.courts || [];
    const currentIndex = courts.findIndex((court) => court.id === courtId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= courts.length) return;

    const reorderedCourts = [...courts];
    [reorderedCourts[currentIndex], reorderedCourts[nextIndex]] = [reorderedCourts[nextIndex], reorderedCourts[currentIndex]];
    setDetailVenue((venue) => ({ ...venue, courts: reorderedCourts }));
    setReorderingCourts(true);
    try {
      await api.put(`/venues/${detailVenue.id}/courts/order`, { courtIds: reorderedCourts.map((court) => court.id) });
      toast.success('Court display order updated');
    } catch (err) {
      setDetailVenue((venue) => ({ ...venue, courts }));
      toast.error(err.response?.data?.message || 'Failed to update court order');
    } finally {
      setReorderingCourts(false);
    }
  };

  const uploadVenuePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !detailVenue) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('isPrimary', 'true');
      await api.post(`/venues/${detailVenue.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Venue photo uploaded and set as primary');
      await openDetail(detailVenue.id);
      setEditingVenue(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deleteVenuePhoto = async (imageUrl) => {
    if (!detailVenue) return;

    setDeletingPhotoUrl(imageUrl);
    try {
      await api.delete(`/venues/${detailVenue.id}/photos`, { params: { imageUrl } });
      toast.success('Venue photo deleted');
      await openDetail(detailVenue.id);
      setEditingVenue(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete photo');
    } finally {
      setDeletingPhotoUrl(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">{venueId ? 'Manage Venue' : 'Venue Partners'}</h1>
        <p className="text-sm text-[#667085] mt-1">{venueId ? 'Update venue details, photos, and courts' : 'Review and manage partner venues'}</p>
      </div>

      {!venueId && partnerApplications.length > 0 && (
        <section className="admin-card border border-amber-200 bg-amber-50/40">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-[#1F2937]">Venue partner applications</h2>
              <p className="mt-1 text-sm text-[#667085]">Accounts registered from the public website. Approve them before they can access the Court Portal.</p>
            </div>
            <span className="admin-badge-warning">{partnerApplications.length} pending</span>
          </div>
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead><tr><th>Applicant</th><th>Email</th><th>Phone</th><th>Verified</th><th>Applied</th><th>Action</th></tr></thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {partnerApplications.map((application) => (
                  <tr key={application.id}>
                    <td className="font-medium text-[#1F2937]">{application.name}</td>
                    <td className="text-[#667085]">{application.email}</td>
                    <td className="text-[#667085]">{application.phoneNumber || '—'}</td>
                    <td><span className={application.isEmailVerified ? 'admin-badge-success' : 'admin-badge-warning'}>{application.isEmailVerified ? 'Verified' : 'Awaiting verification'}</span></td>
                    <td className="text-[#667085]">{formatDate(application.createdAt)}</td>
                    <td><div className="flex flex-wrap gap-2"><button onClick={() => handleApprovePartnerApplication(application.id)} disabled={actionLoading === `partner-${application.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"><CheckCircle size={14} />{actionLoading === `partner-${application.id}` ? 'Processing…' : 'Approve account'}</button><button onClick={() => handleDeclinePartnerApplication(application.id)} disabled={actionLoading === `partner-${application.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50"><XCircle size={14} />Decline</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!venueId && (<div className="admin-card">
        <div className="flex gap-1 bg-[#F9FAFB] p-1 rounded-lg mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={activeTab === tab.key ? 'admin-tab-active' : 'admin-tab-inactive'}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : venues.length === 0 && approvedPartnerAccounts.length === 0 ? (
          <div className="text-center py-12 text-[#667085]">
            <Building2 className="mx-auto mb-2" size={40} />
            <p className="text-sm">No {activeTab} venues</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Venue</th>
                  <th>Owner</th>
                  <th>City</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2B2B2B]">
                {activeTab === 'approved' && approvedPartnerAccounts.map((account) => (
                  <tr key={`partner-account-${account.id}`} className="hover:bg-[#F9FAFB]">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-admin-100 rounded-lg flex items-center justify-center text-admin-700 font-semibold text-sm">
                          <Building2 size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-[#1F2937]">Venue not yet set up</p>
                          <p className="text-xs text-[#667085]">Approved Court Portal account</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-[#667085]">
                      <p>{account.name}</p>
                      <p className="text-xs">{account.email}</p>
                    </td>
                    <td className="text-[#667085]">—</td>
                    <td className="text-[#667085]">{formatDate(account.createdAt)}</td>
                    <td><span className="admin-badge-success">Approved</span></td>
                    <td><span className="text-xs text-[#667085]">Awaiting venue setup</span></td>
                  </tr>
                ))}
                {venues.map((venue) => {
            const status = venue.status?.toLowerCase?.() || '';
            return (
                  <tr key={venue.id} className="hover:bg-[#F9FAFB]">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-admin-100 rounded-lg flex items-center justify-center text-admin-700 font-semibold text-sm">
                          {venue.name?.charAt(0)?.toUpperCase() || 'V'}
                        </div>
                        <div>
                          <p className="font-medium text-[#1F2937]">{venue.name}</p>
                          <p className="text-xs text-[#667085]">{venue.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-[#667085]">{venue.ownerName || 'N/A'}</td>
                    <td className="text-[#667085]">{venue.city}</td>
                    <td className="text-[#667085]">{formatDate(venue.createdAt)}</td>
                    <td>
                      <span className={STATUS_BADGES[status] || 'admin-badge-neutral'}>
                        {venue.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {(status === 'pending' || status === 'suspended') && (
                          <button
                            onClick={() => handleApprove(venue.id)}
                            disabled={actionLoading === venue.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
                          >
                            {actionLoading === venue.id ? (
                              <div className="w-3 h-3 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <CheckCircle size={14} />
                            )}
                            {status === 'suspended' ? 'Reactivate' : 'Approve'}
                          </button>
                        )}
                        {status !== 'suspended' && (
                          <button
                            onClick={() => handleSuspend(venue.id)}
                            disabled={actionLoading === venue.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50"
                          >
                            <XCircle size={14} />
                            Suspend
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/venues/${venue.id}/manage`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-[#F9FAFB] text-[#667085] rounded-lg hover:bg-[#F9FAFB]"
                        >
                          <Pencil size={14} />
                          Manage
                        </button>
                      </div>
                    </td>
                  </tr>
            );
          })}
              </tbody>
            </table>
          </div>
        )}
      </div>)}

      {/* Venue detail — displayed in the page, not in a popup */}
      {venueId && detailLoading && (
        <div className="admin-card flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {venueId && detailVenue && (
        <section className="admin-card">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-[#E4E7EC]">
              <h2 className="text-lg font-bold text-[#1F2937]">{detailVenue.name}</h2>
              <div className="flex items-center gap-3">
                <button onClick={() => setEditingVenue((editing) => !editing)} className="inline-flex items-center gap-1.5 text-sm font-medium text-admin-700 hover:text-admin-800">
                  <Pencil size={15} /> {editingVenue ? 'Close editor' : 'Edit venue'}
                </button>
                <button onClick={() => navigate('/venues')} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#667085] hover:text-[#1F2937]">
                  <ArrowLeft size={16} /> Back to venues
                </button>
              </div>
            </div>

            <div className="pt-6 space-y-6">
              {/* Status badge */}
              <div className="flex items-center gap-3">
                <span className={STATUS_BADGES[(detailVenue.status || '').toLowerCase()] || 'admin-badge-neutral'}>
                  {detailVenue.status}
                </span>
              </div>

              {editingVenue && (
                <form onSubmit={saveVenue} className="rounded-xl border border-admin-200 bg-admin-50 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]"><Pencil size={16} /> Edit venue details</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ['name', 'Venue name'], ['address', 'Address'], ['city', 'City'], ['state', 'State / Province'],
                    ].map(([field, label]) => (
                      <label key={field} className="text-xs font-medium text-[#475467]">{label}
                        <input required={field === 'name' || field === 'address' || field === 'city'} value={venueForm[field] || ''} onChange={(e) => setVenueForm((form) => ({ ...form, [field]: e.target.value }))} className="admin-input mt-1" />
                      </label>
                    ))}
                    <label className="text-xs font-medium text-[#475467]">Price per hour
                      <input required type="number" min="0" step="0.01" value={venueForm.pricePerHour || ''} onChange={(e) => setVenueForm((form) => ({ ...form, pricePerHour: e.target.value }))} className="admin-input mt-1" />
                    </label>
                    <label className="text-xs font-medium text-[#475467]">Maximum hours per transaction
                      <input type="number" min="1" max="24" step="1" placeholder="No limit" value={venueForm.maxBookingHoursPerTransaction || ''} onChange={(e) => setVenueForm((form) => ({ ...form, maxBookingHoursPerTransaction: e.target.value }))} className="admin-input mt-1" />
                    </label>
                    <label className="text-xs font-medium text-[#475467]">Latitude
                      <input type="number" min="-90" max="90" step="any" placeholder="e.g., 14.5995" value={venueForm.latitude || ''} onChange={(e) => setVenueForm((form) => ({ ...form, latitude: e.target.value }))} className="admin-input mt-1" />
                    </label>
                    <label className="text-xs font-medium text-[#475467]">Longitude
                      <input type="number" min="-180" max="180" step="any" placeholder="e.g., 120.9842" value={venueForm.longitude || ''} onChange={(e) => setVenueForm((form) => ({ ...form, longitude: e.target.value }))} className="admin-input mt-1" />
                    </label>
                  </div>
                  <p className="-mt-1 text-xs text-[#667085]">Coordinates are optional, but both are needed to display the venue on the player map.</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-3 rounded-lg border border-[#D0D5DD] bg-white p-3 text-sm text-[#344054]">
                      <input type="checkbox" checked={venueForm.supportsQrPhPayments !== false} onChange={(e) => setVenueForm((form) => ({ ...form, supportsQrPhPayments: e.target.checked }))} className="h-4 w-4" />
                      <span><span className="block font-semibold">QR Ph</span><span className="text-xs text-[#667085]">Available to players at checkout.</span></span>
                    </label>
                    <label className="flex items-center gap-3 rounded-lg border border-[#D0D5DD] bg-white p-3 text-sm text-[#344054]">
                      <input type="checkbox" checked={venueForm.supportsGCashPayments !== false} onChange={(e) => setVenueForm((form) => ({ ...form, supportsGCashPayments: e.target.checked }))} className="h-4 w-4" />
                      <span><span className="block font-semibold">GCash</span><span className="text-xs text-[#667085]">Available to players at checkout.</span></span>
                    </label>
                  </div>
                  <label className="flex items-start gap-3 rounded-lg border border-[#D0D5DD] bg-white p-3 text-sm text-[#344054]">
                    <input type="checkbox" checked={Boolean(venueForm.absorbsPaymentFees)} onChange={(e) => setVenueForm((form) => ({ ...form, absorbsPaymentFees: e.target.checked }))} className="mt-0.5 h-4 w-4" />
                    <span><span className="block font-semibold">Venue absorbs the FREAC booking fee</span><span className="text-xs text-[#667085]">If enabled, the player pays only the rental price and the booking fee is deducted from the venue payout. FREAC covers PayMongo fees and VAT.</span></span>
                  </label>
                  <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-[#344054]">
                    <input type="checkbox" checked={venueForm.payoutPolicy === 'DailyAdvance'} onChange={(e) => setVenueForm((form) => ({ ...form, payoutPolicy: e.target.checked ? 'DailyAdvance' : 'WeeklyAfterCompletion' }))} className="mt-0.5 h-4 w-4" />
                    <span><span className="block font-semibold">Daily advance payouts</span><span className="text-xs text-[#667085]">Paid bookings become payout-eligible immediately, even before play. FREAC funds the venue first and reconciles the PayMongo settlement later.</span></span>
                  </label>
                  <label className="block text-xs font-medium text-[#475467]">Description
                    <textarea value={venueForm.description || ''} onChange={(e) => setVenueForm((form) => ({ ...form, description: e.target.value }))} rows="3" className="admin-input mt-1" />
                  </label>
                  <div className="space-y-3 rounded-lg border border-[#D0D5DD] bg-white p-3">
                    <div>
                      <p className="text-xs font-medium text-[#475467]">Venue photos</p>
                      <p className="mt-1 text-xs text-[#667085]">Uploading a photo sets it as the primary image displayed to players.</p>
                    </div>
                    {(detailVenue.photos?.length > 0 || detailVenue.primaryPhotoUrl) && (
                      <div className="flex flex-wrap gap-3">
                        {[...new Set([detailVenue.primaryPhotoUrl, ...(detailVenue.photos || [])].filter(Boolean))].map((photoUrl) => (
                          <div key={photoUrl} className="group relative h-24 w-24 overflow-hidden rounded-lg border border-[#E4E7EC] bg-[#F9FAFB]">
                            <img src={photoUrl} alt={`${detailVenue.name} venue`} className="h-full w-full object-cover" />
                            {photoUrl === detailVenue.primaryPhotoUrl && (
                              <span className="absolute bottom-1 left-1 rounded bg-[#264D59] px-1.5 py-0.5 text-[10px] font-semibold text-white">Primary</span>
                            )}
                            <button
                              type="button"
                              onClick={() => deleteVenuePhoto(photoUrl)}
                              disabled={deletingPhotoUrl === photoUrl || uploadingPhoto}
                              aria-label="Delete venue photo"
                              className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-600 shadow-sm hover:bg-white disabled:opacity-50"
                            >
                              {deletingPhotoUrl === photoUrl ? <span className="block h-3 w-3 animate-spin rounded-full border-2 border-red-600 border-t-transparent" /> : <X size={13} />}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-xs font-semibold text-admin-700 hover:bg-[#F9FAFB]">
                      {uploadingPhoto ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-admin-700 border-t-transparent" /> : <Upload size={15} />}
                      {uploadingPhoto ? 'Uploading photo...' : 'Upload new primary photo'}
                      <input type="file" accept="image/*" className="sr-only" onChange={uploadVenuePhoto} disabled={uploadingPhoto || deletingPhotoUrl !== null} />
                    </label>
                    <p className="text-xs text-[#667085]">PNG, JPG, or WEBP up to 5MB.</p>
                  </div>
                  <button type="submit" disabled={savingDetails} className="admin-btn-primary">
                    <span className="flex items-center gap-2"><Save size={16} /> {savingDetails ? 'Saving...' : 'Save venue'}</span>
                  </button>
                </form>
              )}

              {/* Info grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Address</p>
                  <p className="text-sm text-[#1F2937]">{detailVenue.address}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">City / Province</p>
                  <p className="text-sm text-[#1F2937]">{detailVenue.city}{detailVenue.state ? `, ${detailVenue.state}` : ''}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Owner</p>
                  <p className="text-sm text-[#1F2937]">{detailVenue.ownerName || 'N/A'}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Price Per Hour</p>
                  <p className="text-sm font-semibold text-[#0B7F83]">{detailVenue.priceRangeLabel || `${formatCurrency(detailVenue.pricePerHour)}/hr`}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Payment Fees</p>
                  <p className="text-sm font-semibold text-[#1F2937]">{detailVenue.absorbsPaymentFees ? 'Booking fee absorbed by venue' : 'Booking fee charged to player'}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Booking Limit</p>
                  <p className="text-sm font-semibold text-[#1F2937]">{detailVenue.maxBookingHoursPerTransaction ? `${detailVenue.maxBookingHoursPerTransaction} hour(s) per transaction` : 'No limit'}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Payout Policy</p>
                  <p className="text-sm font-semibold text-[#1F2937]">{detailVenue.payoutPolicy === 'DailyAdvance' ? 'Daily advance' : 'Weekly after completion'}</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] rounded-xl">
                  <p className="text-xs text-[#667085] uppercase mb-1">Coordinates</p>
                  <p className="text-sm text-[#1F2937]">{detailVenue.latitude || detailVenue.longitude ? `${detailVenue.latitude}, ${detailVenue.longitude}` : 'Not set'}</p>
                </div>
              </div>

              {/* Description */}
              {detailVenue.description && (
                <div>
                  <p className="text-xs text-[#667085] uppercase mb-1">Description</p>
                  <p className="text-sm text-[#1F2937]">{detailVenue.description}</p>
                </div>
              )}

              {/* Amenities */}
              {detailVenue.amenities?.length > 0 && (
                <div>
                  <p className="text-xs text-[#667085] uppercase mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {detailVenue.amenities.map((a, i) => (
                      <span key={i} className="px-3 py-1 bg-[#F9FAFB] rounded-lg text-xs text-[#667085]">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Operating Hours */}
              {detailVenue.operatingHours?.length > 0 && (
                <div>
                  <p className="text-xs text-[#667085] uppercase mb-2">Operating Hours</p>
                  <div className="space-y-1">
                    {groupOperatingHours(detailVenue.operatingHours).map(([day, dayHours]) => {
                      const windows = dayHours.filter((hour) => !hour.isClosed);
                      return <div key={day} className="flex items-start gap-3 text-sm">
                        <span className="w-24 shrink-0 text-[#667085]">{day}</span>
                        <span className="text-[#1F2937]">
                          {windows.length === 0 ? 'Closed' : windows.map((hour) => `${hour.openTime} - ${hour.closeTime}`).join(', ')}
                        </span>
                      </div>
                    })}
                  </div>
                </div>
              )}

              {/* Courts */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#667085] uppercase">Courts ({detailVenue.courtCount})</p>
                  <button onClick={() => { setEditingCourtId(null); setCourtForm({ name: '', courtType: 'Indoor', isActive: true }); setShowCourtForm(true); }} className="inline-flex items-center gap-1 text-xs font-semibold text-admin-700"><Plus size={14} /> Add court</button>
                </div>
                {showCourtForm && (
                  <form onSubmit={saveCourt} className="mb-3 grid grid-cols-1 sm:grid-cols-[1fr_130px_120px_auto] gap-2 rounded-xl bg-[#F9FAFB] p-3">
                    <input required placeholder="Court name" value={courtForm.name} onChange={(e) => setCourtForm((form) => ({ ...form, name: e.target.value }))} className="admin-input" />
                    <select value={courtForm.courtType} onChange={(e) => setCourtForm((form) => ({ ...form, courtType: e.target.value }))} className="admin-input"><option>Indoor</option><option>Outdoor</option></select>
                    <select value={String(courtForm.isActive)} onChange={(e) => setCourtForm((form) => ({ ...form, isActive: e.target.value === 'true' }))} className="admin-input"><option value="true">Active</option><option value="false">Inactive</option></select>
                    <div className="flex gap-2"><button type="submit" disabled={savingDetails} className="admin-btn-primary px-3"><Save size={15} /></button><button type="button" onClick={() => { setEditingCourtId(null); setCourtForm({ name: '', courtType: 'Indoor', isActive: true }); setShowCourtForm(false); }} className="admin-btn-secondary px-3">Cancel</button></div>
                  </form>
                )}
                {detailVenue.courts?.length > 0 ? (
                  <div className="space-y-2">
                    {detailVenue.courts.map((court, index) => (
                      <div key={court.id} className="p-3 bg-[#F9FAFB] rounded-xl">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-0.5">
                            <button onClick={() => moveCourt(court.id, -1)} disabled={index === 0 || reorderingCourts} className="rounded p-1 text-[#667085] hover:bg-white hover:text-admin-700 disabled:cursor-not-allowed disabled:opacity-30" title="Move court up"><ArrowUp size={15} /></button>
                            <button onClick={() => moveCourt(court.id, 1)} disabled={index === detailVenue.courts.length - 1 || reorderingCourts} className="rounded p-1 text-[#667085] hover:bg-white hover:text-admin-700 disabled:cursor-not-allowed disabled:opacity-30" title="Move court down"><ArrowDown size={15} /></button>
                          </div>
                          <Activity className="h-4 w-4 text-[#0B7F83]" />
                          <span className="text-sm text-[#1F2937] font-medium">{court.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-0.5 bg-white rounded text-[#667085]">{court.courtType}</span>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${court.isActive ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#F2F4F7] text-[#475467]'}`}>
                            {court.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <button onClick={() => { setEditingCourtId(court.id); setCourtForm({ name: court.name, courtType: court.courtType || 'Indoor', isActive: court.isActive }); setShowCourtForm(true); }} className="text-admin-700 hover:text-admin-800" title="Edit court"><Pencil size={15} /></button>
                          <button onClick={() => setDeletingCourtId((id) => id === court.id ? null : court.id)} disabled={savingDetails} className="text-red-600 hover:text-red-700 disabled:opacity-50" title="Delete court"><Trash2 size={15} /></button>
                        </div>
                      </div>
                      {deletingCourtId === court.id && (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-xs text-red-800">Delete <strong>{court.name}</strong>? Courts with booking or Open Play history will be deactivated instead.</p>
                          <div className="flex gap-2">
                            <button onClick={() => setDeletingCourtId(null)} className="admin-btn-secondary px-3 py-1.5 text-xs">Cancel</button>
                            <button onClick={() => deleteCourt(court.id)} disabled={savingDetails} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">{savingDetails ? 'Deleting...' : 'Delete court'}</button>
                          </div>
                        </div>
                      )}
                      </div>
                    ))}
                  </div>
                  ) : (
                    <p className="text-sm text-[#667085]">No courts configured</p>
                  )}
                </div>

                {/* Pricing Rules */}
                {detailVenue.pricingRules?.length > 0 && (
                  <div>
                    <p className="text-xs text-[#667085] uppercase mb-2">Time-Based Pricing</p>
                    <div className="space-y-2">
                      {detailVenue.pricingRules.map((rule) => {
                        const fmt = (t) => {
                          if (!t) return '';
                          const [h, m] = (typeof t === 'string' ? t : '').split(':').map(Number);
                          if (isNaN(h)) return t;
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${String(m || 0).padStart(2, '0')} ${ampm}`;
                        };
                        return (
                          <div key={rule.id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-xl">
                            <div className="flex items-center gap-4">
                              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${rule.courtId ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-white text-[#667085]'}`}>
                                {rule.courtName || 'All courts'}
                              </span>
                              <span className="text-xs font-bold text-[#0B7F83] uppercase w-10">{rule.dayOfWeek}</span>
                              <span className="text-sm text-[#1F2937]">{fmt(rule.startTime)} — {fmt(rule.endTime)}</span>
                            </div>
                            <span className="text-sm font-semibold text-[#0B7F83]">
                              {formatCurrency(rule.pricePerHour)}<span className="text-xs text-[#667085] font-normal">/hr</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
        </section>
      )}
    </div>
  );
}
